import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const serviceCategories = [
  {
    name: "Design",
    path: "/services/design",
    items: ["Graphic Design", "Product Shots", "3D Design", "Label & Packaging"]
  },
  {
    name: "Web",
    path: "/services/web",
    items: ["E-commerce Sites", "Local Business Sites", "SEO & Hosting", "Live Chat"]
  },
  {
    name: "Live Chat",
    path: "/services/live-chat",
    items: ["AI Chat Agents", "24/7 Support", "Lead Generation", "Free Trial"]
  },
  {
    name: "Social Marketing",
    path: "/services/social-marketing",
    items: ["TikTok Content", "Short Form Video", "UGC"]
  },
  {
    name: "Digital Marketing",
    path: "/services/digital-marketing",
    items: ["Course Creation", "PDF Courses", "Marketing"]
  },
  {
    name: "AI Services",
    path: "/services/ai-services",
    items: ["Automation", "AI Landing Pages", "AI Tools"]
  },
  {
    name: "Learn Skills",
    path: "/services/learn-skills",
    items: ["TikTok Shop Setup", "Start a Brand", "Online Courses"]
  },
];

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Desktop Navigation */}
      <nav 
        data-testid="main-navigation"
        className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-black/90 backdrop-blur-xl border border-white/10 shadow-lg rounded-full px-6 py-3 hidden md:flex items-center gap-6"
      >
        <Link 
          to="/" 
          data-testid="nav-logo"
          className="flex items-center hover:opacity-90 transition-opacity"
        >
          <img src="/logo.png" alt="Barton" className="h-8 w-auto" />
        </Link>

        <div className="h-6 w-px bg-white/20" />

        <div className="flex items-center gap-1">
          <Link
            to="/"
            data-testid="nav-home"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              isActive("/") 
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" 
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
          >
            Home
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="nav-services-dropdown"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  location.pathname.includes("/services") 
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" 
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                Services <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-64 mt-2 bg-[#111] border-white/10">
              {serviceCategories.map((category, idx) => (
                <div key={category.path}>
                  <DropdownMenuItem asChild>
                    <Link
                      to={category.path}
                      data-testid={`nav-service-${category.path.split('/').pop()}`}
                      className="w-full cursor-pointer font-semibold text-white hover:!text-pink-400 focus:!text-pink-400"
                    >
                      {category.name}
                    </Link>
                  </DropdownMenuItem>
                  <div className="px-2 pb-2">
                    <p className="text-xs text-white/40">{category.items.join(" • ")}</p>
                  </div>
                  {idx < serviceCategories.length - 1 && <DropdownMenuSeparator className="bg-white/10" />}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/work"
            data-testid="nav-work"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              isActive("/work") 
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" 
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
          >
            Work
          </Link>

          <Link
            to="/blog"
            data-testid="nav-blog"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              isActive("/blog") 
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" 
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
          >
            Blog
          </Link>

          <Link
            to="/contact"
            data-testid="nav-contact"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              isActive("/contact") 
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" 
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
          >
            Contact
          </Link>

          <Link
            to="/services/ai-services"
            data-testid="nav-ai-services"
            className="ml-2 px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            AI Services
          </Link>

          <Link
            to="/tiktok-services"
            data-testid="nav-tiktok"
            className="ml-2 px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-pink-500 to-orange-500 text-white hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            TikTok Services
          </Link>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <Link 
            to="/" 
            data-testid="nav-logo-mobile"
            className="flex items-center hover:opacity-90 transition-opacity"
          >
            <img src="/logo.png" alt="Barton" className="h-7 w-auto" />
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/services/ai-services"
              data-testid="mobile-header-ai-services"
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-opacity"
            >
              AI Services
            </Link>
            <Link
              to="/tiktok-services"
              data-testid="mobile-header-tiktok"
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500 to-orange-500 text-white hover:opacity-90 transition-opacity"
            >
              TikTok
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-black border-b border-white/10 shadow-lg animate-fade-in max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-6 space-y-2">
              <Link
                to="/"
                data-testid="mobile-nav-home"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive("/") ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "text-white/80 hover:bg-white/10"
                }`}
              >
                Home
              </Link>

              <div className="pt-2 pb-1 px-4 text-xs font-semibold text-white/40 uppercase tracking-wider">
                Services
              </div>

              {serviceCategories.map((category) => (
                <div key={category.path}>
                  <Link
                    to={category.path}
                    data-testid={`mobile-nav-${category.path.split('/').pop()}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                      isActive(category.path) ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "text-white/80 hover:bg-white/10"
                    }`}
                  >
                    {category.name}
                  </Link>
                  <p className="px-4 text-xs text-white/40 mb-2">{category.items.join(" • ")}</p>
                </div>
              ))}

              <Link
                to="/work"
                data-testid="mobile-nav-work"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive("/work") ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "text-white/80 hover:bg-white/10"
                }`}
              >
                Work
              </Link>

              <Link
                to="/blog"
                data-testid="mobile-nav-blog"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive("/blog") ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "text-white/80 hover:bg-white/10"
                }`}
              >
                Blog
              </Link>

              <div className="pt-4 space-y-3">
                <Link
                  to="/services/ai-services"
                  data-testid="mobile-nav-ai-services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full px-6 py-3 font-medium hover:opacity-90 transition-opacity"
                >
                  AI Services
                </Link>
                <Link
                  to="/tiktok-services"
                  data-testid="mobile-nav-tiktok"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-full px-6 py-3 font-medium hover:opacity-90 transition-opacity"
                >
                  TikTok Services
                </Link>
                <Link
                  to="/contact"
                  data-testid="mobile-nav-contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center border border-white/20 text-white rounded-full px-6 py-3 font-medium hover:bg-white/10 transition-colors"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile spacer */}
      <div className="md:hidden h-16" />
    </>
  );
}
